import React from 'react';
import { PureQueryOptions, ApolloClient } from 'apollo-client';
import P3Campaign from 'models/P3Campaign';
import { CampaignModuleProps } from 'models/interfaces';
import P3CampaignContainer, {
  GET_P3_CAMPAIGN_TEMPLATES_QUERY,
  GET_CLIENT_P3_CAMPAIGN_MODULE,
  PUBLISH_P3CAMPAIGN_MUTATION
} from 'containers/P3CampaignContainer';
import P3CampaignEdit from './P3CampaignEdit';
import {
  DUPLICATE_P3CONFIGURATION_MUTATION,
  GET_P3_CONFIGURATIONS_REDUCED
} from 'containers/P3ConfigurationsContainer';
import P3CampaignPublish from './P3CampaignPublish';
import { inspect } from 'util';
import { Query } from 'react-apollo';
import P3CampaignSaveMachine from 'services/P3CampaignSaveMachine';
import { GET_CAMPAIGN_EDIT_DATA } from 'containers/CampaignEditContainer';
import { deleteP3ConfigurationFromCampaign } from '../P3Templates/P3TemplateEditConfiguration';
import { toast } from 'react-toastify';
import { validateConfiguration } from 'util/p3Validation';

export function refetchQueries(campaignId: string, tgramsId: string) {
  const refetchRp = {
    query: GET_P3_CAMPAIGN_TEMPLATES_QUERY,
    variables: { p3_campaign_id: campaignId, tgrams_id: tgramsId }
  } as PureQueryOptions;

  const refetchThomasCampaign = {
    query: GET_CAMPAIGN_EDIT_DATA,
    variables: { campaignId, tgramsId }
  } as PureQueryOptions;

  return [refetchThomasCampaign, refetchRp];
}

async function getCampaign(apolloClient: ApolloClient<any>) {
  console.log('Getting campaign from cache.');
  let campaignModule: P3Campaign;

  try {
    const data: any = await apolloClient.cache.readQuery({
      query: GET_CLIENT_P3_CAMPAIGN_MODULE
    });

    campaignModule = data.campaignModule;
  } catch (error) {
    console.log('Failed to retrieve campaignModule from cache.');
    console.error(error);
    return null;
  }

  return campaignModule;
}

async function duplicateCampaign(
  p3_configuration_id: string,
  client: ApolloClient<any>,
  campaign_id?: string,
  name?: string,
  startDate?: string,
  endDate?: string,
  previousConfigurationId?: string,
  configurationName?: string
) {
  return await client.mutate({
    mutation: DUPLICATE_P3CONFIGURATION_MUTATION,
    variables: {
      p3_configuration_id,
      campaign_id,
      name,
      startDate,
      endDate,
      previousConfigurationId,
      configurationName
    },
    refetchQueries: [
      {
        query: GET_P3_CONFIGURATIONS_REDUCED
      }
    ]
  });
}

async function getCampaignModuleFromCache(apolloClient: ApolloClient<any>) {
  try {
    const data: any = await apolloClient.cache.readQuery({
      query: GET_CLIENT_P3_CAMPAIGN_MODULE
    });

    return data.campaignModule;
  } catch (error) {
    console.log('Failed to retrieve campaignModule from cache.');
    return null;
  }
}

async function saveCampaign(apolloClient: ApolloClient<any>) {
  let campaignModule: P3Campaign;

  try {
    campaignModule = await getCampaignModuleFromCache(apolloClient);

    if (!campaignModule) {
      throw new Error();
    }
  } catch (error) {
    console.log('Failed to retrieve campaignModule from cache.');
    throw error;
  }

  console.log('GetCampaignModule data: ', campaignModule);

  try {
    const saver = new P3CampaignSaveMachine(apolloClient, campaignModule);
    await saver.save();
  } catch (error) {
    console.log(`Failed to save campaign`, error);
    Promise.reject(false);
  }
  Promise.resolve(true);
}

async function publishCampaign(apolloClient: ApolloClient<any>, campaign: P3Campaign) {
  console.log('Publish campaign: ', inspect(campaign, false, 5));

  const response = await apolloClient.mutate({
    mutation: PUBLISH_P3CAMPAIGN_MUTATION,
    variables: {
      p3CampaignId: campaign.p3_campaign_id
    },
    refetchQueries: refetchQueries(campaign.p3_campaign_id, campaign.tgrams_id)
  });

  console.log("Publish campaign response: ", inspect(response, false, 5));

  if (!response.data.publishP3Campaign) {
    throw new Error('response.data.publishP3Campaign is falsey');
  }

  return true;

}

function renderCampaignEdit({
  campaign,
  companyName,
  startDate,
  endDate,
  productFulfillment,
  onModuleProductNameChange,
  tgramsId
}: CampaignModuleProps) {
  return (
    <P3CampaignContainer p3_campaign_id={campaign.campaign_id} tgrams_id={tgramsId}>
      {({ p3Campaign, templates, updateCampaignModule, client }) => {
        return (
          <P3CampaignEdit
            campaignId={campaign.campaign_id}
            p3Campaign={
              {
                ...p3Campaign
              } /* shallow copy p3Campaign to protect values from no-cache policy */
            }
            campaignStatusId={campaign.status_id}
            startDate={startDate}
            endDate={endDate}
            productFulfillment={productFulfillment}
            onCampaignChange={updateCampaignModule}
            onConfigNameChange={onModuleProductNameChange}
            onRemoveConfiguration={(configurationId: string, campaignId: string ) =>
              deleteP3ConfigurationFromCampaign(
                client,
                configurationId,
                campaignId,
                refetchQueries(campaign.campaign_id, tgramsId),
                () => toast.success('Successfully removed configuration.')
              )
            }
            companyName={companyName}
            tgramsId={tgramsId}
            p3Configurations={
              [...templates] /* shallow copy templates to protect values from no-cache policy */
            }
          />
        );
      }}
    </P3CampaignContainer>
  );
}

function renderCampaignPublish(
  thomasCampaignStatus: number,
  productFulfillment: any,
  publishCampaign: () => any
) {
  return (
    <Query query={GET_CLIENT_P3_CAMPAIGN_MODULE}>
      {({ data: { campaignModule } }) => {
        if (!campaignModule) {
          return null;
        }
        return (
          <P3CampaignPublish
            thomasCampaignStatus={thomasCampaignStatus}
            productFulfillment={productFulfillment}
            startDate={campaignModule.start_date}
            endDate={campaignModule.end_date}
            isConfigurationValid={validateConfiguration(
              campaignModule.p3_configuration,
              campaignModule
            )}
            publishCampaign={publishCampaign}
          />
        );
      }}
    </Query>
  );
}

export default {
  renderCampaignEdit,
  renderCampaignPublish,
  getCampaign,
  duplicateCampaign,
  saveCampaign,
  publishCampaign
};
